import {
  type HelpFeedbackRecord,
  type HelpFeedbackReviewStatus,
  type ListHelpFeedbackResponse,
  type UpdateHelpFeedbackRequest,
  type UpdateHelpFeedbackResponse,
} from '@tcvs/shared';
import { useCallback, useEffect, useState } from 'react';
import { useHelpApi } from '../api';

export const HELP_INSIGHT_STATUSES: readonly HelpFeedbackReviewStatus[] = [
  'NEW',
  'NEEDS_DOCUMENTATION',
  'NEEDS_DIAGNOSTIC',
  'ANSWER_INCORRECT',
  'RESOLVED',
  'IGNORED',
];

export function helpInsightStatusLabel(status: HelpFeedbackReviewStatus): string {
  return status
    .toLowerCase()
    .split('_')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function HelpInsightsPage(): JSX.Element {
  const api = useHelpApi();
  const [status, setStatus] = useState<HelpFeedbackReviewStatus>('NEW');
  const [items, setItems] = useState<HelpFeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.request<ListHelpFeedbackResponse>('/help/feedback', {
        query: { status, limit: 100 },
      });
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setItems(result.data.items);
    } catch {
      setError('Help insights are temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [api, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateStatus(
    feedbackId: string,
    reviewStatus: HelpFeedbackReviewStatus,
  ): Promise<void> {
    setUpdatingId(feedbackId);
    setError(null);
    const body: UpdateHelpFeedbackRequest = { reviewStatus };
    try {
      const result = await api.request<UpdateHelpFeedbackResponse>(
        `/help/feedback/${encodeURIComponent(feedbackId)}`,
        { method: 'PATCH', body },
      );
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setItems((current) => current.filter((item) => item.feedbackId !== feedbackId));
    } catch {
      setError('The insight could not be updated.');
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="tcvs-help-insights">
      <header className="tcvs-section-header">
        <div>
          <h2>Help Insights</h2>
          <p>Review questions where TCVS Help lacked documentation or did not help an operator.</p>
        </div>
        <label>
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as HelpFeedbackReviewStatus)}
          >
            {HELP_INSIGHT_STATUSES.map((item) => (
              <option key={item} value={item}>
                {helpInsightStatusLabel(item)}
              </option>
            ))}
          </select>
        </label>
      </header>

      {error === null ? null : <p className="tcvs-error">Error: {error}</p>}
      {loading ? <p>Loading help insights...</p> : null}
      {!loading && items.length === 0 ? <p>No help insights have this status.</p> : null}
      {!loading && items.length > 0 ? (
        <div className="tcvs-table-wrap">
          <table className="tcvs-table tcvs-help-insights-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Signal</th>
                <th>Page</th>
                <th>Received</th>
                <th>Classify</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.feedbackId}>
                  <td>
                    <strong>{item.question}</strong>
                    {item.answer === undefined ? null : (
                      <details>
                        <summary>Answer shown</summary>
                        <p>{item.answer}</p>
                      </details>
                    )}
                    {item.sources.length === 0 ? null : (
                      <small>
                        Sources: {item.sources.map((source) => source.title).join(', ')}
                      </small>
                    )}
                  </td>
                  <td>{item.signal.replaceAll('_', ' ')}</td>
                  <td>{item.currentRoute ?? 'Unknown'}</td>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  <td>
                    <select
                      aria-label={`Classify ${item.question}`}
                      defaultValue={item.reviewStatus}
                      disabled={updatingId === item.feedbackId}
                      onChange={(event) =>
                        void updateStatus(
                          item.feedbackId,
                          event.target.value as HelpFeedbackReviewStatus,
                        )
                      }
                    >
                      {HELP_INSIGHT_STATUSES.map((reviewStatus) => (
                        <option key={reviewStatus} value={reviewStatus}>
                          {helpInsightStatusLabel(reviewStatus)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
